import { useState, useEffect, useRef } from "react";
import DeleteModal from "../../components/modals/DeleteModal";
import { formattedContainerId } from "../../components/formatted/FormattedText";
import ContainerVisual from "../../components/formatted/LoadImages";
import useWindowDimensions from "../../components/formatted/UseWindowsDimensions";
import { CropImage } from "../../components/utils/ImageTreatment";
import ContainerThumbnailView from "./ContainerThumbnailView";
import ContainerTableView from "./ContainerTableView";
import {
  checkContainerContractExist,
  checkPlanningIsActive,
  handleSelectOne,
} from "./utilsTable";
import { getMostRecentHistory } from "../../shared/utils/utils";
import { useDispatch } from "react-redux";
import { toggleContainer } from "../../redux/containerSlice";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SortingQualityFooter from "../../ui/components/SortingQualityFooter";

const ContainerRow = ({
  containersSelected,
  setContainersSelected,
  setConfirmationCollectModal,
  data,
  displayToBlock,
  formData,
  setFormData,
  setModalEdit,
  fullData,
  tableData,
  setTableData,
  settings,
  screenWidth,
  noActivePackmat,
  toggleImageZoomOverlay,
  containerActive,
  setContainerActive,
  superContainer,
  enrichmentErrors,
  retryEnrichment,
  isEnriching,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const [containers, setContainers] = useState([]);
  const [fillingAlert, setFillingAlert] = useState({});
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const max_session_size = process.env.REACT_APP_MAX_SESSION_SIZE;
  const { widthWindow } = useWindowDimensions();
  const dispatch = useDispatch();
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  let imageWidth = displayToBlock ? (widthWindow < 1320 ? 220 : 240) : 140;
  let imageHeight = displayToBlock ? 350 : widthWindow < 1320 ? 220 : 140;
  let deltaAlert = Math.floor(
    (new Date()?.getTime() - new Date(data._time)?.getTime()) / 86400000,
  );

  const [isLoadingHistoryCycleContainer, setIsLoadingHistoryCycleContainer] =
    useState(true);
  const index = fullData.findIndex((item) => item._id === data._id);
  const [containerData, setContainerData] = useState(() => {
    // Initialize with base data to ensure it's never empty
    return { ...data };
  });

  const containerDataRef = useRef(containerData);
  useEffect(() => {
    containerDataRef.current = containerData;
  }, [containerData]);

  const containerId = data._id;
  const hasError = enrichmentErrors?.has(containerId);

  useEffect(() => {
    if (hasError) {
      setIsLoadingHistoryCycleContainer(true);
      return;
    }

    if (!data.durationFullFillingMs || !data.historyCurrent) {
      setIsLoadingHistoryCycleContainer(true);
      return;
    }

    const dataModal = {
      ...data,
      ...containerDataRef.current,
    };

    setContainerData(dataModal);
    dispatch(toggleContainer({ [containerId]: dataModal }));
    setIsLoadingHistoryCycleContainer(false);
    setFillingAlert(data.fillingAlert || {});
  }, [data, containerId, enrichmentErrors, dispatch, hasError]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Mode tableau : vérifier dropdownRef et buttonRef
      if (dropdownRef.current || buttonRef.current) {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      }
      // Mode bloc : vérifier menuRef
      else if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (superContainer) {
      const filtered = fullData.filter((item) =>
        superContainer.containerObjectIdList.includes(item._id),
      );
      const sortedData = filtered.sort((a, b) => {
        if (
          a.image_url.includes("-R.jpeg") &&
          !b.image_url.includes("-R.jpeg")
        ) {
          return 1;
        } else if (
          !a.image_url.includes("-R.jpeg") &&
          b.image_url.includes("-R.jpeg")
        ) {
          return -1;
        } else {
          return 0;
        }
      });
      setContainers(sortedData);
      setIsLoadingHistoryCycleContainer(false);
    }
  }, [superContainer, fullData]);

  const currentDate = new Date();

  const setFillingWeightColor = (dataWeight, dataFullWeight, weightUnit) => {
    if (weightUnit === `t`) {
      dataFullWeight = dataFullWeight * 1000;
    }
    if (isNaN(dataFullWeight) || dataFullWeight === 0) {
      return "text-grey";
    } else if (dataWeight === dataFullWeight) {
      return "text-blue";
    } else if (dataWeight > dataFullWeight) {
      return "text-red";
    } else if (dataWeight < dataFullWeight) {
      return "text-grey";
    } else {
      return "text-grey";
    }
  };

  const historyCurrent =
    containerData.historyCurrent ?? fullData[index]?.historyCurrent ?? [];
  const weightKG = getMostRecentHistory(historyCurrent)?.mass;

  const dataIsReady = (id) => {
    // Allow opening if we have base data (data object exists)
    // Enriched data (historyCurrent, etc.) can be loaded later in the details page
    if (!data || !data._id) {
      return false;
    }

    // Check if we have historyCurrent in containerData (from unified cache) or in fullData
    if (containerData?.historyCurrent || containerData?.characterization) {
      return true;
    }

    const filteredData = fullData.filter((item) => item._id === id);
    if (filteredData.length > 0) {
      if (filteredData[0]?.historyCurrent || filteredData[0].characterization) {
        return true;
      }
      // Even without historyCurrent, allow if we have basic container data
      if (filteredData[0]._id && filteredData[0].wasteName) {
        return true;
      }
    }

    // Fallback: allow if we have at least the data prop
    return !!(data && data._id);
  };

  const handleOpenNewTabClick = (container) => {
    const _time = new Date().toISOString();
    const filteredData = fullData.filter((item) => item._id === container._id);
    const countContentCategory = fullData.filter(
      (item) =>
        data.contentCategoryJoin.label === item.contentCategoryJoin.label,
    ).length;
    if (superContainer) {
      const containerDetails = {
        _time: _time,
        container: filteredData[0],
        containers: containers,
        superContainer: location.pathname.includes("characterization")
          ? superContainer
          : null,
        countContentCategory: countContentCategory,
        viewType: location.pathname.includes("characterization")
          ? "characterization"
          : "monitoring",
      };
      sessionStorage.setItem(
        "containerDetails",
        JSON.stringify(containerDetails),
      );
      if (location.pathname.includes("characterization")) {
        window.open(`/characterization/${superContainer._id}`, "_blank");
      } else {
        window.open(`/container/${superContainer._id}`, "_blank");
      }
    } else {
      // Use containerData (enriched from cache) merged with base data
      const baseData =
        fullData.find((item) => item._id === container._id) || data;
      let dataContainer = {
        ...baseData,
        ...containerData, // Enriched data from cache takes precedence
      };

      // Ensure we have historyCurrent from containerData if available
      if (containerData.historyCurrent && !dataContainer.historyCurrent) {
        dataContainer.historyCurrent = containerData.historyCurrent;
      }

      const historyCurrentSize = new Blob([
        JSON.stringify(dataContainer.historyCurrent || []),
      ]).size;
      if (historyCurrentSize > max_session_size) {
        delete dataContainer.historyCurrent;
      }
      const containerDetails = {
        _id: container._id,
        _time: _time,
        container: dataContainer,
        containers: tableData,
        countContentCategory: countContentCategory,
        viewType: location.pathname.includes("characterization")
          ? "characterization"
          : "monitoring",
      };
      sessionStorage.setItem(
        "containerDetails",
        JSON.stringify(containerDetails),
      );
      if (location.pathname.includes("characterization")) {
        window.open(`/characterization/${container._id}`, "_blank");
      } else {
        window.open(`/container/${container._id}`, "_blank");
      }
    }
  };

  return (
    <section
      id={`${data._id}`}
      className={`w-full ${displayToBlock ? "border-[1.2px] border-[#D3D3D3] rounded-lg flex flex-col relative" : ""} ${displayToBlock && containerActive && containerActive._id === data._id && "border-solid border-cyan border-2"} ${displayToBlock ? "table-block-card" : "table-block"}`}
      style={{
        filter:
          deltaAlert > settings.alertNumberOfDays ? "grayscale(100%)" : "none",
        position: "relative",
        zIndex: isOpen ? 1000 : "auto",
      }}
    >
      {/* Badge d'erreur avec retry */}
      {hasError && displayToBlock && (
        <div className="absolute top-4 left-4 z-20">
          <button
            onClick={(e) => {
              e.stopPropagation();
              retryEnrichment(containerId);
            }}
            className="flex items-center gap-2 bg-yellow-50 border border-yellow-400 text-yellow-800 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-yellow-100 transition-colors"
            title={t("Retry loading data")}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={isEnriching ? "animate-spin" : ""}
            >
              <path
                d="M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span>{t("Retry")}</span>
          </button>
        </div>
      )}

      {/* Bouton rond 3 points en haut à droite (mode bloc uniquement) */}
      {displayToBlock && (
        <div className="absolute top-4 right-4 z-[1001]" ref={menuRef}>
          <button
            className="w-10 h-10 rounded-full bg-white border-[1.2px] border-gray-300 hover:bg-gray-50 flex items-center justify-center transition-colors"
            onClick={(event) => {
              event.stopPropagation();
              setIsOpen(!isOpen);
            }}
            style={{ padding: 0 }}
          >
            <i
              className="fas fa-ellipsis-v text-gray-600"
              style={{
                lineHeight: 1,
                margin: 0,
                padding: 0,
                display: "inline-block",
                transform: "translateY(1.5px)",
              }}
            ></i>
          </button>

          {isOpen && (
            <div className="absolute right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[180px] z-[9999]">
              <button
                className="w-full px-4 py-2.5 text-left hover:bg-gray-50 text-sm text-gray-700 flex items-center gap-3 transition-colors"
                onClick={(event) => {
                  event.stopPropagation();
                  if (dataIsReady(data._id)) {
                    if (superContainer) {
                      if (containers.length > 1) {
                        handleOpenNewTabClick(containers[0]);
                      }
                    } else {
                      handleOpenNewTabClick(data);
                    }
                    setContainerActive(data);
                  }
                  setIsOpen(false);
                }}
              >
                <i className="fas fa-eye text-gray-500 w-4"></i>
                <span>{t("Detail")}</span>
              </button>
              <button
                className="w-full px-4 py-2.5 text-left hover:bg-gray-50 text-sm text-gray-700 flex items-center gap-3 transition-colors"
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  const id = data._id ?? data._measurement ?? data.id;
                  if (id) {
                    history.push(`/settings/containers/${id}`, {
                      container: data,
                      from: "monitoring",
                      returnPath: location.pathname + location.search,
                    });
                  }
                  setIsOpen(false);
                }}
              >
                <i className="fas fa-edit text-gray-500 w-4"></i>
                <span>{t("Edit")}</span>
              </button>
              {checkContainerContractExist(
                [data],
                [{ _id: data._id, _time: data._time }],
              ) === true && (
                <button
                  className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${!checkPlanningIsActive([data], [{ _id: data._id, _time: data._time }]) ? "opacity-50 cursor-not-allowed text-gray-400" : "hover:bg-gray-50 text-gray-700"}`}
                  disabled={
                    !checkPlanningIsActive(
                      [data],
                      [{ _id: data._id, _time: data._time }],
                    )
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!data.checked) {
                      const simulatedEvent = {
                        target: { checked: true },
                        type: "change",
                      };
                      handleSelectOne(
                        simulatedEvent,
                        data,
                        setContainersSelected,
                        containersSelected,
                      );
                    }
                    setConfirmationCollectModal(true);
                    setIsOpen(false);
                  }}
                >
                  <i className="fas fa-sync-alt text-gray-500 w-4"></i>
                  <span>{t("Request a rotation")}</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {/* Contenu principal: image + texte */}
      <div className={displayToBlock ? "flex flex-row w-full" : "flex"}>
        <div
          className={` ${!displayToBlock ? "data-image-block" : "flex-shrink-0"}`}
          style={{ width: `${displayToBlock ? "auto" : 0}` }}
        >
          <div
            className={`border-[1.2px] border-[#D3D3D3] rounded-xl overflow-hidden ${displayToBlock ? "mt-6 ml-6 mb-6" : ""}`}
          >
            {data.image_url ? (
              <CropImage
                container={data}
                data={data}
                containersSelected={containersSelected}
                setContainersSelected={setContainersSelected}
                imgDivWidth={imageWidth}
                imgDivHeight={imageHeight}
                isTooltip={true}
                isMap={false}
                isCharacterization={false}
                displayLegend={false}
                materialsDropdownToDisplay={[]}
                showCheckbox={!displayToBlock}
                onClickZoomButton={() => {
                  setContainerActive(data);
                  toggleImageZoomOverlay();
                }}
                displayToBlock={displayToBlock}
                setContainerActive={setContainerActive}
              />
            ) : (
              <ContainerVisual type={data.type} width={imageWidth} />
            )}
          </div>
        </div>

        <section
          className={`table-line-data ${displayToBlock ? "flex-1 pl-6" : ""}`}
          style={{
            width: `${displayToBlock ? "100%" : screenWidth > 1280 ? "" : "62%"}`,
          }}
        >
          {displayToBlock ? (
            <ContainerThumbnailView
              data={data}
              weightKG={weightKG}
              numberOfPackmat={
                containerData.numberOfPackmat ??
                fullData[index]?.numberOfPackmat
              }
              currentDate={currentDate}
              isLoadingHistoryCycleContainer={isLoadingHistoryCycleContainer}
              fillingAlert={fillingAlert}
            />
          ) : (
            <div
              className="flex w-full"
              style={{
                padding: "0",
                minHeight: "60px",
                backgroundColor: "#fff",
                borderBottom: "1.5px solid #e0e0e0",
                borderLeft: "1.5px solid #e0e0e0",
                borderRight: "1.5px solid #e0e0e0",
                alignItems: "center",
                margin: 0,
              }}
            >
              <div className="data-table data-monitoring-checkbox">
                <input
                  type="checkbox"
                  className="w-4 h-4 cursor-pointer z-10"
                  style={{ marginLeft: "8px", marginRight: "0px" }}
                  onChange={(event) => {
                    event.stopPropagation();
                    handleSelectOne(
                      event,
                      data,
                      setContainersSelected,
                      containersSelected,
                    );
                  }}
                  checked={data.checked}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <ContainerTableView
                data={data}
                deltaAlert={deltaAlert}
                settings={settings}
                setFillingWeightColor={setFillingWeightColor}
                weightKG={weightKG}
                imageWidth={imageWidth}
                imageHeight={imageHeight}
                screenWidth={screenWidth}
                numberOfStars={
                  containerData.numberOfStars ?? fullData[index]?.numberOfStars
                }
                numberOfAnomaly={
                  containerData.numberOfAnomaly ??
                  fullData[index]?.numberOfAnomaly
                }
                numberOfPackmat={
                  containerData.numberOfPackmat ??
                  fullData[index]?.numberOfPackmat
                }
                durationFullFillingMs={
                  containerData.durationFullFillingMs ??
                  fullData[index]?.durationFullFillingMs
                }
                reliablePrediction={
                  containerData.reliablePrediction ??
                  fullData[index]?.reliablePrediction
                }
                fromDate={containerData.fromDate ?? fullData[index]?.fromDate}
                currentDate={currentDate}
                isLoadingHistoryCycleContainer={isLoadingHistoryCycleContainer}
                noActivePackmat={noActivePackmat}
                fillingAlert={fillingAlert}
                totalVolumeSortingError={
                  containerData.totalVolumeSortingError ??
                  fullData[index]?.totalVolumeSortingError
                }
                totalWasteVolume={
                  containerData.totalWasteVolume ??
                  fullData[index]?.totalWasteVolume
                }
                totalMassSortingError={
                  containerData.totalMassSortingError ??
                  fullData[index]?.totalMassSortingError
                }
                actionButton={
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <div ref={menuRef}>
                      <button
                        ref={buttonRef}
                        className="w-10 h-10 rounded-full bg-white border-[1.2px] border-gray-300 hover:bg-gray-50 flex items-center justify-center transition-colors"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (buttonRef.current) {
                            const rect =
                              buttonRef.current.getBoundingClientRect();
                            const menuWidth = 180;
                            let left = rect.right - menuWidth;

                            // Ajuster si le menu sort de l'écran à gauche
                            if (left < 10) {
                              left = 10;
                            }

                            // Ajuster si le menu sort de l'écran à droite
                            if (left + menuWidth > window.innerWidth - 10) {
                              left = window.innerWidth - menuWidth - 10;
                            }

                            setMenuPosition({
                              top: rect.bottom + 8,
                              left: left,
                            });
                          }
                          setIsOpen(!isOpen);
                        }}
                        style={{ padding: 0, flexShrink: 0 }}
                      >
                        <i
                          className="fas fa-ellipsis-v text-gray-600"
                          style={{
                            lineHeight: 1,
                            margin: 0,
                            padding: 0,
                            display: "inline-block",
                            transform: "translateY(1.5px)",
                          }}
                        ></i>
                      </button>

                      {isOpen && (
                        <div
                          ref={dropdownRef}
                          className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[180px] z-[9999]"
                          style={{
                            top: `${menuPosition.top}px`,
                            left: `${menuPosition.left}px`,
                          }}
                        >
                          <button
                            className="w-full px-4 py-2.5 text-left hover:bg-gray-50 text-sm text-gray-700 flex items-center gap-3 transition-colors"
                            onClick={(event) => {
                              event.stopPropagation();
                              if (dataIsReady(data._id)) {
                                if (superContainer) {
                                  if (containers.length > 1) {
                                    handleOpenNewTabClick(containers[0]);
                                  }
                                } else {
                                  handleOpenNewTabClick(data);
                                }
                                setContainerActive(data);
                              }
                              setIsOpen(false);
                            }}
                          >
                            <i className="fas fa-eye text-gray-500 w-4"></i>
                            <span>{t("Detail")}</span>
                          </button>
                          <button
                            className="w-full px-4 py-2.5 text-left hover:bg-gray-50 text-sm text-gray-700 flex items-center gap-3 transition-colors"
                            onClick={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              const id =
                                data._id ?? data._measurement ?? data.id;
                              if (id) {
                                history.push(`/settings/containers/${id}`, {
                                  container: data,
                                  from: "monitoring",
                                  returnPath:
                                    location.pathname + location.search,
                                });
                              }
                              setIsOpen(false);
                            }}
                          >
                            <i className="fas fa-edit text-gray-500 w-4"></i>
                            <span>{t("Edit")}</span>
                          </button>
                          {checkContainerContractExist(
                            [data],
                            [{ _id: data._id, _time: data._time }],
                          ) === true && (
                            <button
                              className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 text-sm text-gray-700 flex items-center gap-3 transition-colors ${!checkPlanningIsActive([data], [{ _id: data._id, _time: data._time }]) ? "opacity-50 cursor-not-allowed" : ""}`}
                              disabled={
                                !checkPlanningIsActive(
                                  [data],
                                  [{ _id: data._id, _time: data._time }],
                                )
                              }
                              onClick={(event) => {
                                event.stopPropagation();
                                if (
                                  checkPlanningIsActive(
                                    [data],
                                    [{ _id: data._id, _time: data._time }],
                                  )
                                ) {
                                  if (!data.checked) {
                                    const simulatedEvent = {
                                      target: { checked: true },
                                      type: "change",
                                    };
                                    handleSelectOne(
                                      simulatedEvent,
                                      data,
                                      setContainersSelected,
                                      containersSelected,
                                    );
                                  }
                                  setConfirmationCollectModal(true);
                                  setIsOpen(false);
                                }
                              }}
                            >
                              <i className="fas fa-rotate text-gray-500 w-4"></i>
                              <span>{t("Request a rotation")}</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                }
              />
            </div>
          )}

          {/* Modal Delete*/}
          {modalDelete && (
            <DeleteModal
              modal={modalDelete}
              setModal={setModalDelete}
              id={data._id}
              message1={
                t("Are you sure you want to delete this container") +
                " " +
                formattedContainerId(data.containerId) +
                data.containerId +
                " ?"
              }
              message2={
                formattedContainerId(data.containerId) + data.containerId
              }
              messageValidation={t(
                "The container has been successfully deleted",
              )}
              path={"container/delete/"}
              tableData={tableData}
              setTableData={setTableData}
            />
          )}
        </section>
      </div>

      {/* Footer - Erreurs de tri et étoiles - EN DESSOUS DE TOUT */}
      {displayToBlock && data.activatedSortingErrorOption && (
        <SortingQualityFooter
          isLoading={isLoadingHistoryCycleContainer}
          numberOfAnomaly={
            containerData.numberOfAnomaly ?? fullData[index]?.numberOfAnomaly
          }
          numberOfStars={
            containerData.numberOfStars ?? fullData[index]?.numberOfStars
          }
          totalWasteVolume={
            containerData.totalWasteVolume ?? fullData[index]?.totalWasteVolume
          }
          totalVolumeSortingError={
            containerData.totalVolumeSortingError ??
            fullData[index]?.totalVolumeSortingError
          }
          totalMassSortingError={
            containerData.totalMassSortingError ??
            fullData[index]?.totalMassSortingError
          }
        />
      )}
    </section>
  );
};

export default ContainerRow;
